import { Link } from "react-router-dom";
import { ArrowRight, FileCheck, Shield, Eye, BarChart2, Users, CheckCircle } from "lucide-react";
import { motion } from "framer-motion";

const principles = [
  {
    title: "Defined Evidence",
    description: "Every product comes with specified evidence: what has been tested, under what conditions, with what results. We distinguish verified performance from projected capability.",
    icon: FileCheck,
  },
  {
    title: "Model Provenance",
    description: "Where we use models—for simulation, analysis, or AI—we document provenance, confidence levels, and limitations. Users know what drives the outputs.",
    icon: BarChart2,
  },
  {
    title: "Clear Maturity Indicators",
    description: "We distinguish current capability from development roadmap. Research, development, and production-ready are different stages with different evidence requirements.",
    icon: Shield,
  },
  {
    title: "Human Authority",
    description: "Autonomous functions operate within defined bounds. Human operators retain authority over critical decisions. The system supports—it does not replace—human judgement.",
    icon: Users,
  },
];

const evidenceTypes = [
  {
    category: "Simulation & Analysis",
    items: [
      "Model inputs, configurations, and seed values",
      "Statistical outputs with confidence intervals",
      "Limitation statements and applicability boundaries",
      "Reproducibility: any result can be regenerated",
    ],
  },
  {
    category: "Testing & Validation",
    items: [
      "Test conditions and environmental parameters",
      "Performance data with measurement uncertainty",
      "Failure modes identified and documented",
      "Gap analysis against requirements",
    ],
  },
  {
    category: "Deployment & Operations",
    items: [
      "Operational boundaries and constraints",
      "Dependency documentation (regulatory, infrastructure, training)",
      "Monitoring and audit capabilities",
      "Incident and anomaly logging",
    ],
  },
];

const commitments = [
  "We do not claim capabilities we cannot demonstrate",
  "We do not conflate simulation results with operational performance",
  "We do not present development concepts as production-ready",
  "We do not obscure limitations behind marketing language",
  "We do not claim IP protection beyond filed applications",
];

export default function EvidencePage() {
  return (
    <div className="relative">
      {/* Hero */}
      <section className="pt-32 pb-16 relative">
        <div className="absolute inset-0 grid-pattern opacity-50" />
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-caption mb-4">Credibility Through Discipline</p>
            <h1 className="text-display text-foreground mb-6">
              Evidence & Assurance
            </h1>
            <p className="text-subtitle max-w-2xl">
              Credibility comes from discipline, not from marketing claims. Every RLK product comes with defined evidence, model provenance, and clear maturity indicators.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Why This Matters */}
      <section className="section-padding">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="max-w-3xl">
            <h2 className="text-headline text-foreground mb-6">
              Why This Matters
            </h2>
            <p className="text-body mb-4">
              Defence and critical infrastructure customers need to trust the systems they deploy. That trust cannot be built on marketing language or aspirational claims.
            </p>
            <p className="text-body mb-4">
              RLK maintains a discipline of evidence: what has been tested, what the results were, what the limitations are, and what remains to be validated. This discipline extends from simulation and analysis through development, testing, and operational deployment.
            </p>
            <p className="text-body">
              We would rather describe a capability accurately than oversell it. Customers who understand what they are buying make better decisions—and become better long-term partners.
            </p>
          </div>
        </div>
      </section>

      {/* Principles */}
      <section className="section-padding-lg bg-card/30 edge-glow">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="text-center mb-12">
            <p className="text-caption mb-4">Approach</p>
            <h2 className="text-headline text-foreground">Core Principles</h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {principles.map((principle, index) => (
              <motion.div
                key={index}
                className="card-premium p-6"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="h-10 w-10 rounded border border-border bg-muted/50 flex items-center justify-center mb-4">
                  <principle.icon className="h-5 w-5 text-muted-foreground" />
                </div>
                <h3 className="text-label text-foreground mb-2">{principle.title}</h3>
                <p className="text-sm text-muted-foreground">{principle.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Evidence Types */}
      <section className="section-padding-lg">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="text-center mb-12">
            <p className="text-caption mb-4">Documentation</p>
            <h2 className="text-headline text-foreground">What We Document</h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {evidenceTypes.map((type, index) => (
              <motion.div
                key={index}
                className="card-pillar p-6"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <h3 className="text-label text-foreground mb-4">{type.category}</h3>
                <ul className="space-y-2">
                  {type.items.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <div className="h-1.5 w-1.5 rounded-full bg-muted-foreground mt-2 flex-shrink-0" />
                      <span className="text-sm text-muted-foreground">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Commitments */}
      <section className="section-padding-lg bg-card/30 edge-glow">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-headline text-foreground mb-8 text-center">
              Our Commitments
            </h2>
            <div className="card-pillar p-8">
              <ul className="space-y-4">
                {commitments.map((commitment, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-muted-foreground mt-0.5 flex-shrink-0" />
                    <span className="text-body">{commitment}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Maturity Framework */}
      <section className="section-padding-lg">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-headline text-foreground mb-6 text-center">
              Maturity Indicators
            </h2>
            <p className="text-body text-center mb-8">
              We use clear maturity indicators to distinguish development stages:
            </p>
            
            <div className="space-y-4">
              <div className="card-premium p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-label text-foreground">Research</span>
                    <p className="text-sm text-muted-foreground mt-1">Algorithm development, simulation studies, concept exploration</p>
                  </div>
                  <span className="text-xs bg-muted/50 text-muted-foreground px-2 py-1 rounded">Early Stage</span>
                </div>
              </div>
              
              <div className="card-premium p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-label text-foreground">Development</span>
                    <p className="text-sm text-muted-foreground mt-1">Sub-system testing, integration work, design validation</p>
                  </div>
                  <span className="text-xs bg-muted/80 text-foreground px-2 py-1 rounded">In Progress</span>
                </div>
              </div>
              
              <div className="card-premium p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-label text-foreground">Production</span>
                    <p className="text-sm text-muted-foreground mt-1">System-level validation, operational deployment, customer delivery</p>
                  </div>
                  <span className="text-xs bg-foreground/10 text-foreground px-2 py-1 rounded">Deployed</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding-lg bg-card/30 edge-glow">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-headline text-foreground mb-6">
              Request Evidence Packs
            </h2>
            <p className="text-body mb-8">
              Need detailed evidence documentation for a specific product or capability? Contact us to discuss your requirements.
            </p>
            <Link to="/contact?type=evidence" className="btn-primary">
              Request Documentation
              <ArrowRight className="h-4 w-4 ml-2" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
